<template>
    <div class="schemas-refonte min-h-screen bg-gray-50">
        <!-- Banner d'information pour les non-admins -->
        <AdminPermissionBanner />

        <!-- En-tête de la page -->
        <div class="bg-white shadow-sm border-b border-gray-200">
            <div class="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between items-center py-6">
                    <div class="flex items-center space-x-4">
                        <button
                            class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                            @click="$router.push('/admin')">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            <span>Retour</span>
                        </button>
                        <h1 class="text-3xl font-bold text-gray-900">Gestion des Schémas</h1>
                    </div>

                    <!-- Actions principales -->
                    <div class="flex items-center space-x-3">
                        <!-- Boutons collapse/expand -->
                        <button
                            class="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                            title="Replier toutes les sections" @click="collapseAll">
                            <span class="text-sm">▶ Tout replier</span>
                        </button>
                        <button
                            class="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                            title="Déplier toutes les sections" @click="expandAll">
                            <span class="text-sm">▼ Tout déplier</span>
                        </button>

                        <button v-if="canCreate"
                            class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                            @click="openCreateModal">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 4v16m8-8H4"></path>
                            </svg>
                            <span>Nouveau</span>
                        </button>
                        <button v-if="canCreate"
                            class="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                            @click="openImportModal">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10">
                                </path>
                            </svg>
                            <span>Importer</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Contenu principal -->
        <div class="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <!-- Filtre -->
            <div class="mb-6">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-medium text-gray-900">Filtres</h3>
                        <div class="relative">
                            <button
                                class="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                @click="showFilterDropdown = !showFilterDropdown">
                                Filtrer par Type
                                <svg class="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                    fill="currentColor">
                                    <path fill-rule="evenodd"
                                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 01.02-1.06z"
                                        clip-rule="evenodd" />
                                </svg>
                            </button>

                            <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <div v-if="showFilterDropdown"
                                    class="absolute right-0 z-20 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div class="py-2">
                                        <div v-for="type in availableSchemaTypes" :key="type"
                                            class="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                            <div class="flex items-center">
                                                <input :id="`filter-${type}`" v-model="selectedSchemaTypes"
                                                    type="checkbox" :value="type"
                                                    class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                            </div>
                                            <label :for="`filter-${type}`" class="cursor-pointer select-none">
                                                {{ type }}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="py-1">
                                        <button
                                            class="text-xs text-blue-600 hover:text-blue-700 px-4 py-2 flex items-center w-full hover:bg-gray-50"
                                            @click="selectedSchemaTypes = []">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 mr-1.5"
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Réinitialiser les filtres
                                        </button>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 🚀 OPTIMISÉ : Tableau matriciel EXACT de l'original mais avec performance -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200">
                <div class="overflow-x-auto">
                    <table class="w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <!-- En-tête fixe pour la colonne des sections -->
                                <th
                                    class="sticky left-0 z-10 w-80 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-r border-gray-200">
                                    <span>Sections</span>
                                </th>

                                <!-- 🚀 OPTIMISÉ : En-têtes des schémas avec memoization -->
                                <template v-for="(schema, index) in displayedSchemas" :key="schema.uuid">
                                    <!-- Séparateur de groupe -->
                                    <th v-if="isGroupingActive && index > 0 && schema.type !== displayedSchemas[index - 1].type"
                                        class="w-2 bg-gray-300 border-l border-r border-gray-400 p-0"></th>

                                    <th class="min-w-64 px-4 py-3 text-center border-r border-gray-200">
                                        <div class="flex flex-col items-center space-y-3">
                                            <!-- Actions sur le schéma -->
                                            <div class="flex justify-center space-x-1 w-full">
                                                <!-- Exporter -->
                                                <div class="relative group">
                                                    <button
                                                        class="flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors duration-150"
                                                        @click="debouncedExportSchema(schema)">
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4m7-10l4 4m0 0l-4 4m4-4H3">
                                                            </path>
                                                        </svg>
                                                    </button>
                                                    <div
                                                        class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100]">
                                                        Exporter le schéma
                                                    </div>
                                                </div>

                                                <!-- Éditer -->
                                                <div v-if="canEdit" class="relative group">
                                                    <button
                                                        class="flex items-center justify-center w-8 h-8 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded transition-colors duration-150"
                                                        @click="openEditModal(schema)">
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                                            </path>
                                                        </svg>
                                                    </button>
                                                    <div
                                                        class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100]">
                                                        Modifier le schéma
                                                    </div>
                                                </div>

                                                <!-- Supprimer -->
                                                <div v-if="canDelete" class="relative group">
                                                    <button
                                                        class="flex items-center justify-center w-8 h-8 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors duration-150"
                                                        @click="confirmDeleteSchema(schema)">
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                                            </path>
                                                        </svg>
                                                    </button>
                                                    <div
                                                        class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100]">
                                                        Supprimer le schéma
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Actions sur les sections -->
                                            <div v-if="canEdit" class="flex justify-center space-x-1 w-full">
                                                <!-- Sélectionner toutes les sections -->
                                                <div class="relative group">
                                                    <button :class="[
                                                        'flex items-center justify-center px-2 py-1 text-xs rounded transition-all duration-150',
                                                        pendingSelectAll[schema.uuid]
                                                            ? 'bg-green-500 hover:bg-green-600 text-white'
                                                            : 'bg-green-100 hover:bg-green-200 text-green-700'
                                                    ]" @click="handleSelectAllSections(schema)">
                                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2" d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                        {{ pendingSelectAll[schema.uuid] ? 'Confirmer ?' : 'Tout ✓' }}
                                                    </button>
                                                    <div
                                                        class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100]">
                                                        {{ pendingSelectAll[schema.uuid] ? 'Cliquez pour confirmer' :
                                                            'Sélectionner toutes les sections' }}
                                                    </div>
                                                </div>

                                                <!-- Désélectionner toutes les sections -->
                                                <div class="relative group">
                                                    <button :class="[
                                                        'flex items-center justify-center px-2 py-1 text-xs rounded transition-all duration-150',
                                                        pendingDeselectAll[schema.uuid]
                                                            ? 'bg-red-500 hover:bg-red-600 text-white'
                                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                    ]" @click="handleDeselectAllSections(schema)">
                                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                                        </svg>
                                                        {{ pendingDeselectAll[schema.uuid] ? 'Confirmer ?' : 'Tout ✗' }}
                                                    </button>
                                                    <div
                                                        class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100]">
                                                        {{ pendingDeselectAll[schema.uuid] ? 'Cliquez pour confirmer' :
                                                            'Désélectionner toutes les sections' }}
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Informations du schéma -->
                                            <div class="text-center">
                                                <div class="flex items-center justify-center space-x-2">
                                                    <div class="font-semibold text-lg text-gray-900">{{ schema.type }}
                                                    </div>
                                                    <span v-if="hasConformityProblems(schema)" class="text-amber-500"
                                                        title="Ce schéma contient des sections avec des problèmes de conformité">
                                                        ⚠️
                                                    </span>
                                                </div>
                                                <div class="text-sm text-gray-600 font-medium">{{ schema.appellation }}
                                                </div>
                                            </div>

                                        </div>
                                    </th>
                                </template>
                            </tr>
                        </thead>

                        <tbody class="bg-white divide-y divide-gray-200">
                            <!-- 🚀 OPTIMISÉ : Lignes des sections avec performance mais interface EXACTE -->
                            <template v-for="section in sections" :key="section.id">
                                <!-- Masquer cette section si son parent est replié -->
                                <tr v-if="shouldShowSection(section)"
                                    class="hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100">
                                    <!-- Colonne fixe des sections -->
                                    <td class="sticky left-0 z-10 bg-white border-r border-gray-200 px-2 py-2">
                                        <div class="flex items-center justify-between group">
                                            <!-- Affichage hiérarchique tree classique -->
                                            <div class="flex items-center flex-grow min-w-0">
                                                <!-- Indentation avec lignes tree -->
                                                <div class="flex items-center mr-2 flex-shrink-0">
                                                    <!-- Indentation propre avec des lignes -->
                                                    <div v-if="(section.indentationLevel ?? 0) > 0"
                                                        class="flex items-center"
                                                        :style="{ paddingLeft: `${(section.indentationLevel ?? 0) * 20}px` }">
                                                        <!-- Ligne verticale et horizontale -->
                                                        <div class="relative">
                                                            <!-- Ligne horizontale -->
                                                            <div class="w-4 h-px bg-gray-300 absolute top-1/2 left-0">
                                                            </div>
                                                            <!-- Ligne verticale conditionnelle -->
                                                            <div v-if="!isLastChild(section)"
                                                                class="w-px h-6 bg-gray-300 absolute top-0 left-0">
                                                            </div>
                                                            <!-- Coin L pour le dernier enfant -->
                                                            <div v-else
                                                                class="w-px h-3 bg-gray-300 absolute top-0 left-0">
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Bouton collapse/expand pour les sections avec enfants -->
                                                    <button v-if="hasChildren(section)"
                                                        class="ml-2 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-150"
                                                        :title="isCollapsed(section.name) ? 'Déplier' : 'Replier'"
                                                        @click="toggleCollapse(section.name)">
                                                        <span class="text-sm font-mono">{{ isCollapsed(section.name) ?
                                                            '▶' : '▼' }}</span>
                                                    </button>
                                                    <span v-else class="ml-7 w-5"></span>
                                                    <!-- Espace pour l'alignement -->
                                                </div>

                                                <!-- Nom d'affichage avec ellipsis -->
                                                <span class="font-medium text-gray-900 truncate max-w-xs"
                                                    :title="section.name">
                                                    {{ getDisplayName(section.name) }}
                                                </span>
                                            </div>

                                            <!-- Boutons d'action -->
                                            <div v-if="canEdit"
                                                class="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0 ml-2">
                                                <button
                                                    class="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded transition-colors duration-150"
                                                    title="Sélectionner cette section pour tous les schémas"
                                                    @click="selectAllSchemasForSection(section.id)">
                                                    Sél.
                                                </button>
                                                <button
                                                    class="text-xs bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded transition-colors duration-150"
                                                    title="Désélectionner cette section pour tous les schémas"
                                                    @click="deselectAllSchemasForSection(section.id)">
                                                    Désél.
                                                </button>
                                            </div>
                                        </div>
                                    </td>

                                    <!-- 🚀 OPTIMISÉ : Cellules de checkbox pour chaque schéma avec debouncing -->
                                    <template v-for="(schema, index) in displayedSchemas" :key="schema.uuid">
                                        <!-- Séparateur de groupe -->
                                        <td v-if="isGroupingActive && index > 0 && schema.type !== displayedSchemas[index - 1].type"
                                            class="w-2 bg-gray-200 border-l border-r border-gray-300 p-0"></td>

                                        <td class="w-96 px-2 py-2 border-r border-gray-200">
                                            <div class="flex items-center justify-between min-h-[32px]">
                                                <!-- Zone gauche : Checkbox -->
                                                <div class="flex items-center justify-center w-8 ml-5 relative"
                                                    :class="{ 'bg-amber-100 rounded': isSectionProblematic(schema, section) }"
                                                    @mouseenter="isSectionProblematic(schema, section) ? hoveredProblemSection = { schemaUuid: schema.uuid, sectionId: section.id } : null"
                                                    @mouseleave="hoveredProblemSection = null">
                                                    <!-- 🚀 OPTIMISATION CRITIQUE : Checkbox debouncée -->
                                                    <input type="checkbox"
                                                        :checked="schema.section_list.includes(section.id)"
                                                        :disabled="!canEdit"
                                                        class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 transition-colors duration-150"
                                                        :class="[
                                                            canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                                                            isSectionProblematic(schema, section) ? 'border-amber-500' : ''
                                                        ]" @change="debouncedToggleSection(schema.uuid, section.id)" />

                                                    <!-- Tooltip pour les sections problématiques -->
                                                    <div v-if="hoveredProblemSection?.schemaUuid === schema.uuid && hoveredProblemSection?.sectionId === section.id"
                                                        class="absolute z-[100] bg-amber-50 border border-amber-200 p-3 rounded-lg shadow-lg text-left whitespace-normal"
                                                        style="min-width: 250px; left: 50%; transform: translateX(-50%); top: 100%; margin-top: 8px;">
                                                        <div class="text-amber-800 font-medium text-sm mb-1">⚠️ Section
                                                            problématique</div>
                                                        <div class="text-amber-700 text-xs">
                                                            Cette section parente est sélectionnée mais aucune de ses
                                                            sous-sections ne l'est.
                                                            Cela peut créer des incohérences dans le schéma.
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Zone droite : Traductions + bouton ajouter -->
                                                <div
                                                    class="flex items-center space-x-1 min-h-[20px] flex-1 justify-end pr-2 mr-5">
                                                    <template
                                                        v-if="getSchemaTranslationsForSection(schema.uuid, section.id).length > 0">
                                                        <div v-for="translation in getSchemaTranslationsForSection(schema.uuid, section.id)"
                                                            :key="translation.id"
                                                            class="relative cursor-pointer flex items-center"
                                                            @mouseenter="hoveredSchemaTranslation = translation"
                                                            @mouseleave="hoveredSchemaTranslation = null"
                                                            @click="editSchemaTranslation(schema, section, translation)">
                                                            <span
                                                                :class="`fi fi-${getFlagCode(translation.language_code)}`"
                                                                class="text-sm w-4 h-4 flex-shrink-0"></span>

                                                            <!-- Tooltip for translation -->
                                                            <div v-if="hoveredSchemaTranslation === translation"
                                                                class="absolute z-[100] bg-white p-2 rounded shadow-lg border text-left whitespace-normal"
                                                                style="min-width: 200px; left: 50%; transform: translateX(-50%); top: 100%; margin-top: 4px;">
                                                                <div class="font-medium mb-1">{{ translation.titre }}
                                                                </div>
                                                                <div class="text-sm text-gray-600">{{
                                                                    translation.description }}</div>
                                                            </div>
                                                        </div>
                                                    </template>

                                                    <!-- Bouton pour ajouter une traduction -->
                                                    <button v-if="schema.section_list.includes(section.id) && canEdit"
                                                        class="rounded-full w-4 h-4 flex items-center justify-center bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-800 transition-colors duration-150 flex-shrink-0 ml-1"
                                                        title="Ajouter une traduction pour cette section du schéma"
                                                        @click="openSchemaTranslationModal(schema, section)">
                                                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor"
                                                            viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2" d="M12 4v16m8-8H4"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </template>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Modal de confirmation de suppression -->
        <Teleport to="body">
            <div v-if="showConfirmationModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"
                    @click="closeConfirmationModal"></div>
                <div class="flex min-h-full items-center justify-center p-4">
                    <div
                        class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24"
                                        stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c.77.833 1.732 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Supprimer le schéma</h3>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            Êtes-vous sûr de vouloir supprimer le schéma "{{ schemaToDelete?.appellation
                                            }}" ?
                                            Cette action est irréversible.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button type="button"
                                class="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto transition-colors duration-150"
                                @click="handleDeleteSchema">
                                Supprimer
                            </button>
                            <button type="button"
                                class="mt-3 inline-flex justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 sm:mt-0 sm:w-auto transition-colors duration-150"
                                @click="closeConfirmationModal">
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- Modal de traduction spécifique à un schéma et une section -->
        <Teleport to="body">
            <div v-if="showSchemaTranslationModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"></div>
                <div class="flex min-h-full items-center justify-center p-4">
                    <div
                        class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <form @submit.prevent="handleSaveSchemaTranslation">
                            <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <h3 class="text-lg font-medium leading-6 text-gray-900 mb-4">
                                    {{ editingSchemaTranslation ? 'Modifier la traduction' : 'Ajouter une traduction' }}
                                </h3>

                                <!-- Info contexte -->
                                <div class="mb-4 p-3 bg-gray-50 rounded-lg">
                                    <div class="text-sm text-gray-600">
                                        <strong>Schéma:</strong> {{ currentSchemaForTranslation?.appellation }} ({{
                                            currentSchemaForTranslation?.type }})
                                    </div>
                                    <div class="text-sm text-gray-600">
                                        <strong>Section:</strong> {{ currentSectionForTranslation?.name }}
                                    </div>
                                </div>

                                <!-- Sélection de la langue -->
                                <div class="mb-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Langue</label>
                                    <select v-model="translationFormData.language_code"
                                        class="w-full p-2 border rounded-md" :disabled="!!editingSchemaTranslation">
                                        <option v-for="lang in availableLanguagesForSchemaSection" :key="lang.code"
                                            :value="lang.code">
                                            {{ lang.name }} ({{ lang.code }})
                                        </option>
                                    </select>
                                </div>

                                <!-- Champ titre -->
                                <div class="mb-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                                    <input v-model="translationFormData.titre" type="text"
                                        class="w-full p-2 border rounded-md" placeholder="Entrez le titre" />
                                </div>

                                <!-- Champ description -->
                                <div class="mb-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea v-model="translationFormData.description"
                                        class="w-full p-2 border rounded-md" rows="3"
                                        placeholder="Entrez la description"></textarea>
                                </div>
                            </div>

                            <!-- Boutons d'action -->
                            <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button type="submit"
                                    class="inline-flex justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto transition-colors duration-150">
                                    Sauvegarder
                                </button>
                                <button type="button"
                                    class="mt-3 inline-flex justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 sm:mt-0 sm:w-auto transition-colors duration-150"
                                    @click="closeSchemaTranslationModal">
                                    Annuler
                                </button>
                                <button v-if="editingSchemaTranslation" type="button"
                                    class="mt-3 inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:mt-0 sm:w-auto sm:mr-auto transition-colors duration-150"
                                    @click="handleDeleteSchemaTranslation">
                                    Supprimer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- Modal de confirmation pour décochage en cascade -->
        <Teleport to="body">
            <div v-if="showHierarchyConfirmation" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"></div>
                <div class="flex min-h-full items-center justify-center p-4">
                    <div
                        class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                        <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div class="flex">
                                <div
                                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c.77.833 1.732 2.5 1.732 2.5zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                </div>
                                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <h3 class="text-base font-semibold leading-6 text-gray-900">Désélection en cascade
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            La section <strong>{{ hierarchyConfirmationData.section?.name }}</strong> a
                                            <strong>{{ hierarchyConfirmationData.childrenToDeselect.length }}</strong>
                                            sous-section(s) sélectionnée(s).
                                        </p>
                                        <p class="text-sm text-gray-500 mt-2">
                                            Voulez-vous également désélectionner automatiquement toutes les
                                            sous-sections ?
                                        </p>
                                        <div class="mt-3 text-xs text-gray-400">
                                            <strong>Sous-sections concernées :</strong>
                                            <ul class="list-disc list-inside mt-1">
                                                <li v-for="child in hierarchyConfirmationData.childrenToDeselect"
                                                    :key="child.id">
                                                    {{ child.name }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="button"
                                class="inline-flex justify-center rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 sm:ml-3 sm:w-auto transition-colors duration-150"
                                @click="confirmHierarchyDeselection">
                                Oui, désélectionner tout
                            </button>
                            <button type="button"
                                class="mt-3 inline-flex justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 sm:mt-0 sm:w-auto transition-colors duration-150"
                                @click="closeHierarchyConfirmation">
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- 🚀 CORRECTION : Composants modals restaurés -->
        <!-- Modal d'édition/création de schéma -->
        <SchemaEditModal v-model="showSchemaModal" :schema-data="editingSchema || undefined" :is-editing="isEditingMode"
            @save="handleSaveSchemaForm" @delete="handleDeleteFromModal" />

        <!-- Modal d'import -->
        <SchemaImportModal v-model="showImportModal" @import-success="handleImportSuccess" />
    </div>
</template>

<script setup lang="ts">
// 🚀 COMPOSITION API OPTIMISÉE - Interface EXACTE de l'original avec performance
import { computed, onMounted, ref } from 'vue';

// Stores et services
import { useAuthStore } from '@/stores/auth';
import { useSchemasStore } from '@/stores/schemasStore';

// 🚀 CORRECTION : API services manquants
import { sections as sectionsAPI, translations } from '@/api';
import { fetchConfigsAdmin } from '@/api/field_config';
import {
    schemaTranslations,
    type SchemaTranslationWithDetails
} from '@/api/schemaTranslations';
import type { ImportReport } from '@/api/types/core/schemaJson';
import type { SchemaTranslationCreate } from '@/api/types/core/schemaTranslations';

// 🚀 NOUVEAUX : Utilitaires de performance optimisés
import {
    debounce,
    measurePerformance
} from '@/utils/performanceUtils';

// Types
import type { Section } from '@/api/types/core/sections';
import type { SchemaWithSectionList } from '@/stores/schemasStore';

// 🚀 CORRECTION : Composants manquants
import AdminPermissionBanner from '@/components/admin/AdminPermissionBanner.vue';
import SchemaEditModal from '@/components/admin/SchemaEditModal.vue';
import SchemaImportModal from '@/components/admin/SchemaImportModal.vue';

// 🚀 CORRECTION : CSS flags manquant
import '/node_modules/flag-icons/css/flag-icons.min.css';

// =================== STORES ET COMPOSABLES ===================
const authStore = useAuthStore();
const schemasStore = useSchemasStore();

// =================== ÉTAT RÉACTIF OPTIMISÉ ===================

// 🚀 CORRECTION : Computed standards Vue.js pour réactivité correcte
const schemas = computed(() => schemasStore.schemas);
const sections = computed(() => schemasStore.organizedSections);

// const loading = computed(() => schemasStore.loading); // Non utilisé pour le moment

// 🚀 OPTIMISATION : Filtres avec debouncing pour réduire les recalculs
const selectedSchemaTypes = ref<string[]>([]);
const showFilterDropdown = ref(false);

// Computed pour types disponibles
const availableSchemaTypes = computed(() => {
    const types = new Set(schemas.value.map(s => s.type));
    return Array.from(types).sort();
});

// 🚀 OPTIMISATION CRITIQUE : displayedSchemas memoizé
const displayedSchemas = computed(() => {
    let filtered: SchemaWithSectionList[];
    if (selectedSchemaTypes.value.length === 0) {
        filtered = schemas.value;
    } else {
        filtered = schemas.value.filter(schema =>
            selectedSchemaTypes.value.includes(schema.type)
        );
    }
    // Tri par type puis appellation
    filtered = [...filtered].sort((a, b) => {
        if (a.type < b.type) return -1;
        if (a.type > b.type) return 1;
        if (a.appellation < b.appellation) return -1;
        if (a.appellation > b.appellation) return 1;
        return 0;
    });
    // Log pour debug
    console.log('Ordre des schémas (type/appellation) :', filtered.map(s => `${s.type} / ${s.appellation}`));
    return filtered;
});

const isGroupingActive = computed(() => selectedSchemaTypes.value.length === 0);

// Permissions computed
const canCreate = computed(() => authStore.user?.role === 'admin');
const canEdit = computed(() => authStore.user?.role === 'admin');
const canDelete = computed(() => authStore.user?.role === 'admin');

// =================== ÉTAT DES MODALS ===================

// Modal de confirmation de suppression
const showConfirmationModal = ref(false);
const schemaToDelete = ref<SchemaWithSectionList | null>(null);

// Modal de création/édition de schéma
const showSchemaModal = ref(false);
const editingSchema = ref<SchemaWithSectionList | null>(null);
const isEditingMode = ref(false);

// Modal de traduction spécifique à un schéma et une section
const showSchemaTranslationModal = ref(false);
const currentSchemaForTranslation = ref<SchemaWithSectionList | null>(null);
const currentSectionForTranslation = ref<Section | null>(null);
const editingSchemaTranslation = ref<SchemaTranslationWithDetails | null>(null);
const translationFormData = ref<SchemaTranslationCreate>({
    schema_uuid: '',
    section_id: 0,
    language_code: '',
    titre: '',
    description: ''
});

// Langues disponibles




// Modal d'import
const showImportModal = ref(false);

// Collapse/expand optimisé avec Set pour performance
const collapsedSections = ref(new Set<string>());

// États pour les actions groupées
const pendingSelectAll = ref<Record<string, boolean>>({});
const pendingDeselectAll = ref<Record<string, boolean>>({});

// Hover states
const hoveredProblemSection = ref<{ schemaUuid: string; sectionId: number } | null>(null);
const hoveredSchemaTranslation = ref<SchemaTranslationWithDetails | null>(null);

// =================== MÉTHODES OPTIMISÉES ===================

// 🚀 OPTIMISATION CRITIQUE : Toggle section debouncé avec gestion en cascade
const debouncedToggleSection = debounce(async (schemaUuid: string, sectionId: number) => {
    await measurePerformance('Toggle Section', async () => {
        const schema = schemas.value.find(s => s.uuid === schemaUuid);
        const section = sections.value.find(s => s.id === sectionId);

        if (!schema || !section) return;

        const isCurrentlySelected = schema.section_list.includes(sectionId);

        try {
            if (isCurrentlySelected) {
                // Désélection : vérifier s'il y a des enfants sélectionnés
                await handleSectionDeselection(schema, section);
            } else {
                // Sélection : auto-sélectionner les parents si nécessaire
                await handleSectionSelection(schema, section);
            }
        } catch (error) {
            console.error('Erreur lors du toggle section:', error);
        }
    });
}, 150);

// 🆕 Gestion du décochage en cascade avec warning
const showHierarchyConfirmation = ref(false);
const hierarchyConfirmationData = ref<{
    schema: SchemaWithSectionList | null;
    section: Section | null;
    childrenToDeselect: Section[];
}>({
    schema: null,
    section: null,
    childrenToDeselect: []
});

const handleSectionDeselection = async (schema: SchemaWithSectionList, section: Section): Promise<void> => {
    const selectedChildren = getSelectedChildrenSections(schema, section);

    if (selectedChildren.length > 0) {
        // Afficher le modal de confirmation
        hierarchyConfirmationData.value = {
            schema,
            section,
            childrenToDeselect: selectedChildren
        };
        showHierarchyConfirmation.value = true;
    } else {
        // Pas d'enfants, désélection directe
        await performSectionDeselection(schema, section, []);
    }
};

const handleSectionSelection = async (schema: SchemaWithSectionList, section: Section): Promise<void> => {
    try {
        // 1. Sélectionner la section courante
        await schemasStore.addSectionToSchema(schema.uuid, section.id);

        // 2. 🚀 OPTIMISATION : Auto-sélectionner tous les parents manquants
        const parentsToSelect = getParentSectionsToSelect(schema, section);
        for (const parentSection of parentsToSelect) {
            await schemasStore.addSectionToSchema(schema.uuid, parentSection.id);
        }

        const message = parentsToSelect.length > 0
            ? `Section sélectionnée avec ${parentsToSelect.length} section(s) parente(s) automatiquement ajoutée(s).`
            : "Section sélectionnée avec succès.";

        console.log('✅', message);
    } catch (error) {
        console.error('❌ Erreur lors de la sélection de section:', error);
    }
};

// 🚀 OPTIMISATION : Calcul des parents manquants uniquement pour la section modifiée
const getParentSectionsToSelect = (schema: SchemaWithSectionList, section: Section): Section[] => {
    const parentsToSelect: Section[] = [];
    const sectionPath = section.name;
    const pathParts = sectionPath.split('.');

    // Parcourir tous les niveaux parents
    for (let i = pathParts.length - 1; i > 0; i--) {
        const parentPath = pathParts.slice(0, i).join('.');
        const parentSection = sections.value.find(s => s.name === parentPath);

        if (parentSection && !schema.section_list.includes(parentSection.id)) {
            parentsToSelect.unshift(parentSection); // Ajouter au début pour l'ordre hiérarchique
        }
    }

    return parentsToSelect;
};

const getSelectedChildrenSections = (schema: SchemaWithSectionList, section: Section): Section[] => {
    const childrenSelected: Section[] = [];
    const sectionPrefix = section.name + '.';

    // Parcourir toutes les sections pour trouver les enfants sélectionnés
    for (const otherSection of sections.value) {
        if (otherSection.name.startsWith(sectionPrefix) &&
            schema.section_list.includes(otherSection.id)) {
            childrenSelected.push(otherSection);
        }
    }

    return childrenSelected;
};

const performSectionDeselection = async (schema: SchemaWithSectionList, section: Section, childrenToDeselect: Section[]): Promise<void> => {
    try {
        // Désélectionner la section principale
        await schemasStore.removeSectionFromSchema(schema.uuid, section.id);

        // Désélectionner tous les enfants
        for (const child of childrenToDeselect) {
            await schemasStore.removeSectionFromSchema(schema.uuid, child.id);
        }

        const message = childrenToDeselect.length > 0
            ? `Section désélectionnée avec ${childrenToDeselect.length} sous-section(s) automatiquement supprimée(s).`
            : "Section désélectionnée avec succès.";

        console.log('✅', message);
    } catch (error) {
        console.error('❌ Erreur lors de la désélection:', error);
    }
};

const confirmHierarchyDeselection = (): void => {
    if (hierarchyConfirmationData.value.schema && hierarchyConfirmationData.value.section) {
        performSectionDeselection(
            hierarchyConfirmationData.value.schema,
            hierarchyConfirmationData.value.section,
            hierarchyConfirmationData.value.childrenToDeselect
        );
    }
    closeHierarchyConfirmation();
};

const closeHierarchyConfirmation = (): void => {
    showHierarchyConfirmation.value = false;
    hierarchyConfirmationData.value = {
        schema: null,
        section: null,
        childrenToDeselect: []
    };
};

// =================== DÉTECTION DE PROBLÈMES DE CONFORMITÉ ===================

// 🚀 OPTIMISATION : Détection des problèmes uniquement pour un schéma spécifique
const getSectionProblems = (schema: SchemaWithSectionList): Section[] => {
    const problemSections: Section[] = [];

    for (const section of sections.value) {
        if (schema.section_list.includes(section.id)) {
            // Vérifier si cette section a des enfants
            const hasChildren = sections.value.some(s => s.name.startsWith(section.name + '.'));

            if (hasChildren) {
                // Vérifier si au moins un enfant est sélectionné
                const hasSelectedChild = sections.value.some(s =>
                    s.name.startsWith(section.name + '.') &&
                    schema.section_list.includes(s.id)
                );

                if (!hasSelectedChild) {
                    problemSections.push(section);
                }
            }
        }
    }

    return problemSections;
};

// 🚀 OPTIMISATION : Export de schéma debouncé
const debouncedExportSchema = debounce(async (schema: SchemaWithSectionList) => {
    await measurePerformance('Export Schema', async () => {
        await exportSchema(schema);
    });
}, 300);

// 🚀 CORRECTION : Méthode exportSchema manquante
const exportSchema = async (schema: SchemaWithSectionList): Promise<void> => {
    try {
        console.log(`📦 Export du schéma: ${schema.appellation}`);

        const [allSections, allTranslations, allConfigs] = await Promise.all([
            sectionsAPI.fetchAllSections(),
            translations.fetchTranslations(),
            fetchConfigsAdmin()
        ]);

        console.log(`📋 Total sections récupérées: ${allSections.length}`);
        console.log(`🌐 Total traductions récupérées: ${allTranslations.length}`);
        console.log(`⚙️ Total configs récupérées: ${allConfigs.length}`);

        // 🚀 UTILISATION DU NOUVEL UTILITAIRE DE COMPILATION
        const {
            compileSchemaForExport,
            generateExportFilename,
            countSchemaSpecificTranslations
        } = await import('@/utils/schemaExportUtils');

        // Compiler le schéma avec fusion intelligente des traductions
        const schemaJson = compileSchemaForExport(
            schema,
            allSections,
            allTranslations,
            schemasStore.schemaTranslations,
            allConfigs
        );

        // Générer le nom de fichier
        const filename = generateExportFilename(schema);

        // Créer et télécharger le fichier
        const jsonBlob = new Blob([JSON.stringify(schemaJson, null, 2)], {
            type: 'application/json'
        });

        const url = URL.createObjectURL(jsonBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log(`✅ Export généré avec fusion intelligente des traductions:`, schemaJson);

        // Compter les traductions schema-spécifiques pour le message
        const schemaSpecificTranslationsCount = countSchemaSpecificTranslations(
            schema.uuid,
            schemasStore.schemaTranslations
        );

        console.log(
            `✅ Schéma "${schema.appellation}" exporté avec succès (${schemaJson.section_list.length} sections${schemaSpecificTranslationsCount > 0 ? `, ${schemaSpecificTranslationsCount} traductions spécifiques fusionnées` : ''})`
        );

    } catch (error) {
        console.error("❌ Erreur lors de l'export du schéma:", error);
    }
};

// =================== GESTION DES SCHÉMAS ===================

const openCreateModal = (): void => {
    editingSchema.value = null;
    isEditingMode.value = false;
    showSchemaModal.value = true;
};

const openEditModal = (schema: SchemaWithSectionList): void => {
    editingSchema.value = { ...schema };
    isEditingMode.value = true;
    showSchemaModal.value = true;
};

const closeSchemaModal = (): void => {
    showSchemaModal.value = false;
    editingSchema.value = null;
    isEditingMode.value = false;
};

const handleSaveSchemaForm = async (schemaData: { appellation: string; type: string; description: string }): Promise<void> => {
    await measurePerformance('Save Schema', async () => {
        try {
            if (isEditingMode.value && editingSchema.value) {
                await schemasStore.updateSchema(editingSchema.value.uuid, {
                    appellation: schemaData.appellation,
                    type: schemaData.type,
                    description: schemaData.description,
                    section_list: editingSchema.value.section_list
                });
                console.log('✅ Schéma mis à jour avec succès');
            } else {
                await schemasStore.createSchema({
                    appellation: schemaData.appellation,
                    type: schemaData.type,
                    description: schemaData.description
                });
                console.log('✅ Schéma créé avec succès');
            }

            closeSchemaModal();
            await refreshData();
        } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde du schéma:', error);
        }
    });
};

const confirmDeleteSchema = (schema: SchemaWithSectionList): void => {
    schemaToDelete.value = schema;
    showConfirmationModal.value = true;
};

const closeConfirmationModal = (): void => {
    showConfirmationModal.value = false;
    schemaToDelete.value = null;
};

const handleDeleteSchema = async (): Promise<void> => {
    if (!schemaToDelete.value) return;

    await measurePerformance('Delete Schema', async () => {
        try {
            await schemasStore.deleteSchema(schemaToDelete.value!.uuid);
            console.log('✅ Schéma supprimé avec succès');
            await refreshData();
        } catch (error) {
            console.error('❌ Erreur lors de la suppression du schéma:', error);
        }
    });

    closeConfirmationModal();
};

// 🚀 CORRECTION : Nouvelle méthode pour gérer la suppression depuis SchemaEditModal
const handleDeleteFromModal = async (uuid: string): Promise<void> => {
    await measurePerformance('Delete Schema From Modal', async () => {
        try {
            await schemasStore.deleteSchema(uuid);
            console.log('✅ Schéma supprimé avec succès depuis le modal d\'édition');
            closeSchemaModal();
            await refreshData();
        } catch (error) {
            console.error('❌ Erreur lors de la suppression du schéma depuis le modal:', error);
        }
    });
};

// =================== GESTION DES SECTIONS ===================

// Actions groupées pour toutes les sections d'un schéma
const handleSelectAllSections = (schema: SchemaWithSectionList): void => {
    if (pendingSelectAll.value[schema.uuid]) {
        // Confirmer la sélection
        selectAllSectionsForSchema(schema);
        pendingSelectAll.value[schema.uuid] = false;
    } else {
        // Première pression - demander confirmation
        pendingSelectAll.value[schema.uuid] = true;
        // Reset automatique après 3 secondes
        setTimeout(() => {
            pendingSelectAll.value[schema.uuid] = false;
        }, 3000);
    }
};

const handleDeselectAllSections = (schema: SchemaWithSectionList): void => {
    if (pendingDeselectAll.value[schema.uuid]) {
        // Confirmer la désélection
        deselectAllSectionsForSchema(schema);
        pendingDeselectAll.value[schema.uuid] = false;
    } else {
        // Première pression - demander confirmation
        pendingDeselectAll.value[schema.uuid] = true;
        // Reset automatique après 3 secondes
        setTimeout(() => {
            pendingDeselectAll.value[schema.uuid] = false;
        }, 3000);
    }
};

const selectAllSectionsForSchema = async (schema: SchemaWithSectionList): Promise<void> => {
    await measurePerformance('Select All Sections', async () => {
        try {
            const sectionsToAdd = sections.value
                .filter(section => !schema.section_list.includes(section.id))
                .map(section => section.id);

            for (const sectionId of sectionsToAdd) {
                await schemasStore.addSectionToSchema(schema.uuid, sectionId);
            }

            console.log(`✅ Toutes les sections ajoutées au schéma ${schema.appellation}`);
        } catch (error) {
            console.error('❌ Erreur lors de la sélection de toutes les sections:', error);
        }
    });
};

const deselectAllSectionsForSchema = async (schema: SchemaWithSectionList): Promise<void> => {
    await measurePerformance('Deselect All Sections', async () => {
        try {
            const sectionsToRemove = [...schema.section_list];

            for (const sectionId of sectionsToRemove) {
                await schemasStore.removeSectionFromSchema(schema.uuid, sectionId);
            }

            console.log(`✅ Toutes les sections supprimées du schéma ${schema.appellation}`);
        } catch (error) {
            console.error('❌ Erreur lors de la désélection de toutes les sections:', error);
        }
    });
};

// Actions groupées pour une section sur tous les schémas
const selectAllSchemasForSection = async (sectionId: number): Promise<void> => {
    await measurePerformance('Select All Schemas For Section', async () => {
        try {
            const schemasToUpdate = schemas.value.filter(
                schema => !schema.section_list.includes(sectionId)
            );

            for (const schema of schemasToUpdate) {
                await schemasStore.addSectionToSchema(schema.uuid, sectionId);
            }

            console.log(`✅ Section ${sectionId} ajoutée à tous les schémas`);
        } catch (error) {
            console.error('❌ Erreur lors de la sélection pour tous les schémas:', error);
        }
    });
};

const deselectAllSchemasForSection = async (sectionId: number): Promise<void> => {
    await measurePerformance('Deselect All Schemas For Section', async () => {
        try {
            const schemasToUpdate = schemas.value.filter(
                schema => schema.section_list.includes(sectionId)
            );

            for (const schema of schemasToUpdate) {
                await schemasStore.removeSectionFromSchema(schema.uuid, sectionId);
            }

            console.log(`✅ Section ${sectionId} supprimée de tous les schémas`);
        } catch (error) {
            console.error('❌ Erreur lors de la désélection pour tous les schémas:', error);
        }
    });
};

// =================== GESTION DES TRADUCTIONS ===================

const openSchemaTranslationModal = (schema: SchemaWithSectionList, section: Section): void => {
    currentSchemaForTranslation.value = schema;
    currentSectionForTranslation.value = section;
    editingSchemaTranslation.value = null;
    translationFormData.value = {
        schema_uuid: schema.uuid,
        section_id: section.id,
        language_code: availableLanguagesForSchemaSection.value[0]?.code || 'fr',
        titre: '',
        description: ''
    };
    showSchemaTranslationModal.value = true;
};

const editSchemaTranslation = (schema: SchemaWithSectionList, section: Section, translation: SchemaTranslationWithDetails): void => {
    currentSchemaForTranslation.value = schema;
    currentSectionForTranslation.value = section;
    editingSchemaTranslation.value = translation;
    translationFormData.value = {
        schema_uuid: schema.uuid,
        section_id: section.id,
        language_code: translation.language_code,
        titre: translation.titre,
        description: translation.description || ''
    };
    showSchemaTranslationModal.value = true;
};

const closeSchemaTranslationModal = (): void => {
    showSchemaTranslationModal.value = false;
    currentSchemaForTranslation.value = null;
    currentSectionForTranslation.value = null;
    editingSchemaTranslation.value = null;
    translationFormData.value = {
        schema_uuid: '',
        section_id: 0,
        language_code: '',
        titre: '',
        description: ''
    };
};

const handleSaveSchemaTranslation = async (): Promise<void> => {
    await measurePerformance('Save Schema Translation', async () => {
        try {
            if (editingSchemaTranslation.value) {
                await schemaTranslations.updateTranslation(editingSchemaTranslation.value.id, {
                    titre: translationFormData.value.titre,
                    description: translationFormData.value.description
                });
                console.log('✅ Traduction mise à jour avec succès');
            } else {
                await schemaTranslations.createTranslation(translationFormData.value);
                console.log('✅ Traduction créée avec succès');
            }

            // 🚀 OPTIMISATION : Recharger seulement les traductions schéma
            await schemasStore.refreshSchemaTranslations();

            closeSchemaTranslationModal();
            console.log('🎯 Traduction sauvegardée et interface mise à jour');
        } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde de la traduction:', error);
        }
    });
};

const handleDeleteSchemaTranslation = async (): Promise<void> => {
    if (!editingSchemaTranslation.value) return;

    await measurePerformance('Delete Schema Translation', async () => {
        try {
            await schemaTranslations.deleteTranslation(editingSchemaTranslation.value!.id);
            console.log('✅ Traduction supprimée avec succès');
            closeSchemaTranslationModal();
            await refreshData();
        } catch (error) {
            console.error('❌ Erreur lors de la suppression de la traduction:', error);
        }
    });
};

// Récupérer les traductions d'une section pour un schéma spécifique
const getSchemaTranslationsForSection = (schemaUuid: string, sectionId: number): SchemaTranslationWithDetails[] => {
    return schemasStore.schemaTranslations.filter(t =>
        t.schema_model.uuid === schemaUuid && t.section.id === sectionId
    );
};

// Langues disponibles pour le modal de traduction
const availableLanguagesForSchemaSection = computed(() => {
    const allLanguages = [
        { code: 'fr', name: 'Français' },
        { code: 'en', name: 'Anglais' },
        { code: 'es', name: 'Espagnol' },
        { code: 'de', name: 'Allemand' },
        { code: 'it', name: 'Italien' },
        { code: 'pt', name: 'Portugais' }
    ];

    if (!currentSchemaForTranslation.value || !currentSectionForTranslation.value) {
        return allLanguages;
    }

    // Filtrer les langues déjà utilisées pour cette combinaison schéma/section
    const existingTranslations = getSchemaTranslationsForSection(
        currentSchemaForTranslation.value.uuid,
        currentSectionForTranslation.value.id
    );

    const usedLanguageCodes = existingTranslations.map(t => t.language_code);

    // Si on modifie une traduction existante, inclure sa langue
    if (editingSchemaTranslation.value) {
        usedLanguageCodes.splice(usedLanguageCodes.indexOf(editingSchemaTranslation.value.language_code), 1);
    }

    return allLanguages.filter(lang => !usedLanguageCodes.includes(lang.code));
});



// =================== GESTION DE L'IMPORT ===================

const openImportModal = (): void => {
    showImportModal.value = true;
};

// closeImportModal maintenant géré par SchemaImportModal

// Les méthodes d'import sont maintenant gérées par SchemaImportModal

// 🚀 CORRECTION : Méthode handleImportSuccess manquante
const handleImportSuccess = (report: ImportReport): void => {
    console.log('🎉 Import réussi:', report);
    console.log(
        `✅ Schéma importé avec succès ! ${report.sections_created} sections créées, ${report.sections_updated} mises à jour.`
    );

    refreshData();
};

// =================== UTILITAIRES D'AFFICHAGE ===================

// Collapse/expand
const toggleCollapse = (sectionName: string): void => {
    if (collapsedSections.value.has(sectionName)) {
        collapsedSections.value.delete(sectionName);
    } else {
        collapsedSections.value.add(sectionName);
    }
};

const collapseAll = (): void => {
    const allParentSections = sections.value
        .filter(section => hasChildren(section))
        .map(section => section.name);
    collapsedSections.value = new Set(allParentSections);
};

const expandAll = (): void => {
    collapsedSections.value.clear();
};

const isCollapsed = (sectionName: string): boolean => {
    return collapsedSections.value.has(sectionName);
};

// Hiérarchie des sections
const hasChildren = (section: Section): boolean => {
    return sections.value.some(s =>
        s.name !== section.name && s.name.startsWith(section.name + '.')
    );
};

const shouldShowSection = (section: Section): boolean => {
    const nameParts = section.name.split('.');

    // Toujours afficher les sections de niveau 1
    if (nameParts.length === 1) return true;

    // Pour les sous-sections, vérifier si leur parent est replié
    for (let i = 1; i < nameParts.length; i++) {
        const parentName = nameParts.slice(0, i).join('.');
        if (isCollapsed(parentName)) {
            return false;
        }
    }

    return true;
};

const isLastChild = (section: Section): boolean => {
    const nameParts = section.name.split('.');
    if (nameParts.length === 1) return false;

    const parentName = nameParts.slice(0, -1).join('.');
    const siblings = sections.value.filter(s =>
        s.name.startsWith(parentName + '.') &&
        s.name.split('.').length === nameParts.length
    );

    return siblings[siblings.length - 1]?.name === section.name;
};

const getDisplayName = (sectionName: string): string => {
    const parts = sectionName.split('.');
    return parts[parts.length - 1];
};

// 🚀 OPTIMISATION : Détection des problèmes de conformité avec cache
const isSectionProblematic = (schema: SchemaWithSectionList, section: Section): boolean => {
    // Vérification rapide : section sélectionnée + a des enfants + aucun enfant sélectionné
    if (!schema.section_list.includes(section.id)) return false;

    const hasChildren = sections.value.some(s => s.name.startsWith(section.name + '.'));
    if (!hasChildren) return false;

    const hasSelectedChild = sections.value.some(s =>
        s.name.startsWith(section.name + '.') &&
        schema.section_list.includes(s.id)
    );

    return !hasSelectedChild;
};

const hasConformityProblems = (schema: SchemaWithSectionList): boolean => {
    return getSectionProblems(schema).length > 0;
};

// Utilitaires pour drapeaux de langue
const getFlagCode = (languageCode: string): string => {
    const flagMap: Record<string, string> = {
        'en': 'gb',
        'es': 'es',
        'de': 'de',
        'it': 'it',
        'pt': 'pt',
        'fr': 'fr'
    };
    return flagMap[languageCode] || languageCode;
};

// =================== DONNÉES ET ACTUALISATION ===================

const refreshData = async (forceRefresh = false): Promise<void> => {
    await measurePerformance('Refresh Data', async () => {
        try {
            await schemasStore.initializeSchemasData(forceRefresh);
        } catch (error) {
            console.error('❌ Erreur lors de l\'actualisation:', error);
        }
    });
};

// =================== LIFECYCLE OPTIMISÉ ===================

onMounted(async () => {
    console.log('🚀 Initialisation SchemasNew optimisée...');

    await measurePerformance('Schemas Initialization', async () => {
        try {
            await schemasStore.initializeSchemasData();
            console.log('✅ SchemasNew initialisé avec succès');

            // Statistiques de performance si disponibles
            const cacheStats = schemasStore.getCacheStats?.();
            if (cacheStats) {
                console.log('📊 Cache stats:', cacheStats);
            }
        } catch (error) {
            console.error('❌ Erreur initialisation SchemasNew:', error);
        }
    });
});
</script>

<style scoped>
/* Styles pour l'interface matricielle avec scroll optimisé */
.schemas-refonte {
    min-height: 100vh;
}

/* Table responsive optimisée */
.overflow-x-auto {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
}

.overflow-x-auto::-webkit-scrollbar {
    height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
    background: #f7fafc;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background-color: #a0aec0;
}

/* Colonnes fixes pour la performance */
.sticky {
    position: sticky;
    background-color: white;
}

/* Transitions optimisées */
.transition-colors {
    transition-property: background-color, border-color, color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}

/* Hover states */
.group:hover .opacity-0 {
    opacity: 1;
}

/* Tree lines styling */
.relative::before {
    content: '';
    position: absolute;
    border-left: 1px solid #e5e7eb;
    height: 100%;
    left: 0;
    top: 0;
}

/* Flags styling */
.fi {
    width: 16px;
    height: 16px;
    display: inline-block;
    background-size: cover;
    border-radius: 2px;
}

/* Modal animations */
.fixed.inset-0 {
    backdrop-filter: blur(2px);
}

/* Performance hints */
.will-change-transform {
    will-change: transform;
}

.contain-layout {
    contain: layout;
}
</style>
